/* eslint-disable no-unused-vars */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { defaultFilterIssues, DropdownOption, FilterIssues, Pages } from '@sl/components';
import { DocumentDetail, ISSUE, Issue, IssueStatus } from '@sl/features';
import { DownloadService, NavigationService, SpinnerService } from '@sl/services';
import { SharedModule } from '@sl/shared';
import { trackBy } from '@sl/utils';
import dayjs from 'dayjs';
import { filter, Observable } from 'rxjs';
import { IssueMessage } from 'src/app/core/features/issue/domain/issueMessage.model';
import { IssueMessageDoc } from 'src/app/core/features/issue/domain/IssueMessageDoc.model';
import { GetIssuesFilter } from 'src/app/core/features/issue/infrastructure/repositories/issue.repository.interface';
import { IssueStore } from 'src/app/core/services/stores/issue.store';
import { SendIssueMessageData, SummaryData } from 'src/app/modules/communications/components';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'sl-tab-issues',
  templateUrl: './tab-issues.component.html',
  styleUrls: ['./tab-issues.component.scss'],
  standalone: true,
  imports: [SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabIssuesComponent {
  @Input() orderId: string = this.navigationService.getParam('orderId');

  issues$ = this.issueStore.selectIssues();

  issuesTable: IssueTableItem[] = [];
  totalTablePages!: number;
  pageIndex!: number;
  pageSize!: number;

  isLoading$!: Observable<any>;
  isDownloading = false;
  showErrorModal = false;

  // Filters
  dateFilter = {
    startDate: null as Date | null,
    endDate: null as Date | null
  };

  buttonFilter: FilterIssues = { ...defaultFilterIssues, orderId: this.orderId };

  // Table features
  modalCommunication = {
    show: false,
    issue: null as Issue | null,
    summary: null as SummaryData | null
  };

  // Api params
  issueFilter: GetIssuesFilter = {
    orderId: this.orderId,
    startDate: null,
    endDate: null,
    claimId: null,
    policeId: null,
    insuredName: null,
    type: null,
    clientId: null
  };

  dropdownStatus = {
    label: 'Filtrar',
    placeholder: 'Estado',
    options: [
      { label: 'Abierta', value: ISSUE.STATUS.OPEN },
      { label: 'Cerrada', value: ISSUE.STATUS.CLOSE },
      { label: 'Todas', value: ISSUE.STATUS.ALL }
    ] as DropdownOption<IssueStatus>[],
    value: null as IssueStatus | null
  };

  // ???
  selectedRow: any = {};
  selectedRowIndex!: number; // Not set anywhere but used as notes index in modal
  cellsToShow = ['type', 'nis', 'policy', 'openingDate', 'hour', 'state'];

  showFilters = false;
  stateOptions = ['pending', 'closed'];
  showModal: 'detail' | 'secondCommunications' | '' = '';

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly navigationService: NavigationService,
    private readonly issueStore: IssueStore,
    private readonly downloadService: DownloadService,
    private readonly spinnerService: SpinnerService,
    private readonly translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.getTableData();
    this.initIssuesTable();
  }

  // Filters
  onDatePickerDates(dates: Date[]): void {
    this.dateFilter.startDate = dates[0];
    this.dateFilter.endDate = dates[1];
    this.getTableData();
  }

  onDatePickerRemove(): void {
    this.dateFilter.startDate = null;
    this.dateFilter.endDate = null;
    this.getTableData();
  }

  onSubmitFilters(filters: GetIssuesFilter): void {
    this.buttonFilter = { ...filters };
    this.getTableData();
  }

  onFilterByStatus() {
    if (this.dropdownStatus.value === ISSUE.STATUS.ALL) {
      this.buttonFilter.status = null;
    } else {
      this.buttonFilter.status = this.dropdownStatus.value;
    }
    this.getTableData();
  }

  onDiscardFilters(filters: Partial<GetIssuesFilter>): void {
    this.buttonFilter = { ...filters };
    this.getTableData();
  }

  // Download
  onDownloadTable(): void {
    this.downloadAllIssues();
  }

  // Table features
  onRowClick(data: Record<string, any>): void {
    this.showModalCommunications(data);
  }

  async onSendIssueMessageModal({ documentsWithoutIds, comments, issue }: SendIssueMessageData): Promise<void> {
    await this.handleSendIssueMessage({ documentsWithoutIds, comments, issue });
  }

  onMarkIssueMessageAsRead(issue: Issue): void {
    this.markIssueMessageAsRead(issue);
  }

  onCloseModalCommunication(): void {
    this.closeModalCommunications();
  }

  onChangePage({ pageSize, pageIndex }: Pages): void {
    this.issueFilter.pageSize = pageSize;
    this.issueFilter.pageIndex = pageIndex;
    this.getTableData();
  }

  // Utils

  trackByFn(index: number, item: any): any {
    return trackBy(index, item);
  }

  /* Private */

  // Api
  private getIssues(issueFilter?: GetIssuesFilter): void {
    this.issueStore.actionGetIssues({ issueFilter });
  }

  // private putIssue(issue: Issue): void {
  //   this.isLoading$ = this.spinnerService.getLoadingObservable('issues');
  //   this.issueStore.actionPutIssue({ issue });
  // }

  private async getIssuesPromise(issueFilter: GetIssuesFilter): Promise<Issue[]> {
    return this.issueStore.actionGetIssuesPromise({ issueFilter });
  }

  private async putIssuePromise(issue: Issue): Promise<void> {
    this.isLoading$ = this.spinnerService.getLoadingObservable(issue.id);
    return this.issueStore.actionPutIssuePromise({ issue });
  }

  // Initialization
  private initIssuesTable(): void {
    this.isLoading$ = this.spinnerService.getLoadingObservable('issues');
    this.issues$
      .pipe(
        untilDestroyed(this),
        filter(({ issues, loading, error }) => {
          if (error) {
            this.showErrorModal = true;
          }
          return Boolean(issues) && !loading && !error;
        })
      )
      .subscribe(({ issues }) => {
        this.issuesTable = issues!.map((issue) => this.mapIssueToTableItem(issue));
        const issueUpdated = issues!.find(({ id }) => id === this.modalCommunication.issue?.id);
        if (issueUpdated) {
          this.modalCommunication.issue = issueUpdated;
        }
        // this.totalTablePages = Math.ceil(total! / pageSize!);
        // this.pageIndex = pageIndex!;
        // this.pageSize = pageSize!;
        this.cdr.detectChanges();
      });
  }

  // Download table feature
  private async downloadAllIssues(): Promise<void> {
    this.isDownloading = true;
    try {
      const { pageSize, pageIndex, ...filters } = this.issueFilter;
      const issues = await this.getIssuesPromise(filters);
      const issuesTable = issues?.map((issue) => this.mapDataToDownloadTableItem(issue));
      this.downloadTable(issuesTable);
    } catch (e) {
      console.error(e);
      this.showErrorModal = true;
    } finally {
      this.isDownloading = false;
      this.cdr.detectChanges();
    }
  }

  private downloadTable(table: any): void {
    this.downloadService.exportAsExcel(table, 'segundas_comunicaciones', 'communications');
  }

  // Send issue message feature
  private async handleSendIssueMessage({ documentsWithoutIds, comments, issue }: SendIssueMessageData): Promise<void> {
    const docList: IssueMessageDoc[] = documentsWithoutIds.map((doc) => this.mapDocumentDetailToIssueMessageDoc(doc));

    const newMessage = new IssueMessage({
      messageId: null,
      readDate: new Date(),
      creationDate: new Date(),
      read: true,
      content: comments,
      type: ISSUE.MESSAGE.TYPE.ISSUE,
      subject: null,
      source: ISSUE.MESSAGE.SOURCE.PROFESIONAL,
      sender: null,
      docList
    });

    const allMessagesAsRead = issue.messages.map((message) =>
      message.read ? message : new IssueMessage({ ...message, read: true, readDate: new Date() })
    );

    const issueWithNewMessage: Issue = { ...issue, messages: [...allMessagesAsRead, newMessage] };

    try {
      await this.putIssuePromise(issueWithNewMessage);
      this.getTableData();
    } catch (e) {
      console.error(e);
      this.showErrorModal = true;
    } finally {
      this.cdr.detectChanges();
    }
  }

  // Mark issue message as read feature
  private async markIssueMessageAsRead(issue: Issue): Promise<void> {
    try {
      await this.putIssuePromise(issue);
      this.getTableData();
    } catch (e) {
      console.error(e);
      this.showErrorModal = true;
    } finally {
      this.cdr.detectChanges();
    }
  }

  private showModalCommunications(data: Record<string, any>): void {
    const { value, nis, policy, hour, openDate, sort, closingDate, closingHour } = data;
    const state = this.translocoService.translate('issueStatus.' + value.status).replace('issueStatus.', '');
    this.modalCommunication.summary = { nis, openDate, closingDate, state, policy, hour, closingHour, sort }; // The order is important
    this.modalCommunication.issue = value as Issue;
    this.modalCommunication.show = true;
    this.disableScroll();
  }

  private closeModalCommunications(): void {
    this.modalCommunication.summary = null;
    this.modalCommunication.issue = null;
    this.modalCommunication.show = false;
    this.enableScroll();
  }

  private getTableData(): void {
    const startDate = this.dateFilter.startDate ? mapDateToApi(this.dateFilter.startDate) : null;
    const endDate = this.dateFilter.endDate ? mapDateToApi(this.dateFilter.endDate) : null;
    this.issueFilter = { ...this.issueFilter, ...this.buttonFilter, startDate, endDate, orderId: this.orderId };
    this.getIssues(this.issueFilter);
  }

  // Utils
  private disableScroll(): void {
    document.body.style.overflow = 'hidden';
  }

  private enableScroll(): void {
    document.body.style.overflow = 'auto';
  }

  // Mappers
  private mapIssueToTableItem = (issue: Issue): IssueTableItem => ({
    value: issue,
    sort: this.translocoService.translate('issueType.' + issue.type).replace('issueType.', ''),
    nis: issue.nis,
    policy: issue.policy,
    openDate: issue.date ? mapApiToDate(issue.date) : '',
    hour: issue.date ? mapDateToHour(issue.date) : '',
    closingDate: issue.closingDate ? mapApiToDate(issue.closingDate) : '',
    closingHour: issue.closingDate ? mapDateToHour(issue.closingDate) : '',
    state: issue.status,
    deceasedName: issue.affectedName
  });

  private mapDocumentDetailToIssueMessageDoc = (doc: DocumentDetail): IssueMessageDoc => ({
    docId: doc.documentId,
    docName: doc.name,
    docType: doc.type,
    creationDate: doc.date,
    metadata: doc.content
  });

  private mapDataToDownloadTableItem = (item: Issue): DownloadTableItem => {
    const { value, ...downloadTableItem } = this.mapIssueToTableItem(item);
    return { ...downloadTableItem };
  };
}

const mapApiToDate = (orderDate: any): string => dayjs(orderDate).utc().format('DD/MM/YYYY');
const mapDateToApi = (orderDate: any): string => dayjs(orderDate).format('YYYY-MM-DDTHH:mm:ss[Z]');
const mapDateToHour = (orderDate: any): string => dayjs(orderDate).utc().format('HH:mm');

type IssueTableItem = {
  value: Issue;
  sort: string;
  nis: string;
  policy: string;
  openDate: string;
  hour: string;
  closingDate: string;
  closingHour: string;
  state: string;
  deceasedName: string;
};

export type DownloadTableItem = Omit<IssueTableItem, 'value'>;
